/**
 * public-inference.ts — public-API adapter for the analysis pipeline
 * (PRI-496 Lane B, web cutover slice A).
 *
 * Replaces the legacy internal /v1/video/* + /v1/jobs/* pipeline with the
 * published public resources:
 *
 *   presign+upload      → POST /v1/videos → S3 PUT → POST /v1/videos/{id}/complete
 *   run-prompt / upload → POST /v1/analyses {video_id, prompt|query}
 *   status / result     → GET /v1/analyses/{id} (single read powers both pollers
 *                         via a short micro-cache — the dual-poller UI keeps its
 *                         shape but stops double-fetching)
 *   cancel              → POST /v1/analyses/{id}/cancel
 *   active-job restore  → GET /v1/analyses?status=analyzing|queued
 *   annotated playback  → Analysis.artifacts.annotated_video_url (sign-on-read)
 *   source playback     → Video.media.url (sign-on-read)
 *   per-video history   → GET /v1/videos/{id}/analyses
 *
 * The exported functions keep the LEGACY shapes the hooks already consume
 * (PrimateVisionJob / PrimateVisionResult / ActiveJob), so useInference /
 * useAnalysis / useSessionRestore migrate by changing imports — not logic.
 *
 * Auth rides the shared SDK transport (Clerk JWT or anon pv_live_ key via the
 * token registry) — the same triple-accept path an API customer uses.
 */
import { sdk } from './sdk'
import { PrimateError } from '@primate-intelligence/sdk/browser'
import type { Analysis, AnalysisResult, Page, Video } from '@primate-intelligence/sdk/browser'
import { internalApiUrl } from '../internal-api-client'
import { deriveResultSemantics } from '../result-semantics'
import type { PrimateVisionJob, PrimateVisionResult } from './inference'
import type { ActiveJob } from './misc'
import type { CompiledQuery } from '../../components/v2/compiled-query-types'

// ── Id detection ─────────────────────────────────────────────────────────────

/** True for public analysis ids (an_<ULID>). Legacy UUID job ids return false. */
export function isPublicAnalysisId(id: string): boolean {
  return /^an_[0-9A-HJKMNP-TV-Z]{26}$/i.test(id)
}

/** True for public video ids (video_<ULID>). */
export function isPublicVideoId(id: string): boolean {
  return /^video_[0-9A-HJKMNP-TV-Z]{26}$/i.test(id)
}

// ── Legacy error-shape preservation ──────────────────────────────────────────

/** Re-throw a PrimateError with the legacy {code, status, upgradeUrl} shape. */
function toLegacyError(e: unknown): never {
  if (e instanceof PrimateError) {
    const fallback = /^HTTP \d+$/.test(e.rawMessage) ? `API error: ${e.status}` : e.rawMessage
    throw Object.assign(new Error(fallback), {
      code: e.code,
      status: e.status,
      upgradeUrl: (e.details as { upgrade_url?: string } | undefined)?.upgrade_url,
    })
  }
  throw e
}

// ── Answer casing ────────────────────────────────────────────────────────────
//
// The public contract returns lowercase answers ('yes'|'no'|'indeterminate');
// the legacy plane returned title-case 'Yes'/'No' and several UI surfaces
// render the string verbatim (result pills, tracked events typed 'Yes'|'No').
// Normalize to the legacy casing so the cutover is pixel-identical;
// 'indeterminate' was always lowercase on both planes.
function toLegacyAnswer(answer: string | null | undefined): string {
  if (answer == null) return ''
  const a = answer.toLowerCase().trim()
  if (a === 'yes') return 'Yes'
  if (a === 'no') return 'No'
  return answer
}

// ── Status/stage vocabulary mapping (public → legacy UI) ─────────────────────

function toLegacyStatus(s: Analysis['status']): PrimateVisionJob['status'] {
  switch (s) {
    case 'completed': return 'complete'
    case 'failed': return 'failed'
    case 'canceled': return 'cancelled'
    default: return 'processing' // queued | analyzing
  }
}

/** Public progress stage → the legacy vocabulary useInference's step-mapper reads. */
function toLegacyStage(stage: string | undefined): string {
  switch (stage) {
    case 'queued': return 'queued'
    case 'preparing': return 'preparing'
    case 'analyzing': return 'predicting'
    case 'rendering': return 'rendering'
    default: return stage ?? 'unknown'
  }
}

// ── Micro-cache: coalesce the dual pollers' reads into one GET per tick ──────

const RETRIEVE_CACHE_MS = 900
let retrieveCache: { id: string; at: number; promise: Promise<Analysis> } | null = null

function retrieveAnalysis(id: string): Promise<Analysis> {
  const now = Date.now()
  if (retrieveCache && retrieveCache.id === id && now - retrieveCache.at < RETRIEVE_CACHE_MS) {
    return retrieveCache.promise
  }
  const promise = sdk.analyses.retrieve(id).catch(e => {
    // Never cache failures.
    if (retrieveCache?.promise === promise) retrieveCache = null
    return toLegacyError(e)
  })
  retrieveCache = { id, at: now, promise }
  return promise
}

/** Test hook — reset the retrieve micro-cache between cases. */
export function __clearAnalysisMicroCache(): void {
  retrieveCache = null
}

// ── Status / progress / result (poller surface) ──────────────────────────────

export async function getVisionJobStatus(analysisId: string): Promise<PrimateVisionJob> {
  const a = await retrieveAnalysis(analysisId)
  return {
    job_id: a.id,
    status: toLegacyStatus(a.status),
    answer: a.result?.answer != null ? toLegacyAnswer(a.result.answer) : undefined,
    confidence: a.result?.confidence ?? null,
    error_message: a.error?.message ?? undefined,
  }
}

export async function getJobProgress(analysisId: string): Promise<{
  job_id: string
  status: string
  progress: { stage: string; pct: number; message: string }
  queue_position?: number | null
  error_message?: string | null
}> {
  const a = await retrieveAnalysis(analysisId)
  const status = toLegacyStatus(a.status)
  let progress: { stage: string; pct: number; message: string }
  if (status === 'complete') {
    progress = { stage: 'complete', pct: 100, message: 'Analysis complete' }
  } else if (a.progress) {
    progress = {
      stage: toLegacyStage(a.progress.stage),
      pct: a.progress.percent ?? 0,
      message: a.progress.stage ? `Stage: ${a.progress.stage}` : '',
    }
  } else if (a.status === 'queued') {
    progress = { stage: 'queued', pct: 1, message: 'Waiting for inference server...' }
  } else {
    progress = { stage: 'unknown', pct: 0, message: '' }
  }
  return {
    job_id: a.id,
    status,
    progress,
    queue_position: a.queue_position ?? null,
    error_message: a.error?.message ?? null,
  }
}

function mapResult(a: Analysis): PrimateVisionResult {
  const r: AnalysisResult | null = a.result
  const clips = r?.clips
    ? r.clips.map(c => ({
        start_time: c.start_s,
        end_time: c.end_s,
        confidence: c.confidence,
        ...(c.label != null ? { label: c.label } : {}),
      }))
    : null

  // PRI-488 F2 semantics: the public API already applies read-time semantics
  // server-side (detected_count / indeterminate_reason live on the result).
  // Run the shared client derivation only as a fallback for fields the server
  // omitted (keeps web pixel-identical with what an API customer computes).
  const semantics = deriveResultSemantics({
    answer: r?.answer ?? null,
    confidence: r?.confidence ?? null,
    termConfidences: r?.term_confidences,
    resultJson: (r as unknown as Record<string, unknown>) ?? {},
  })

  // Legacy behavior: indeterminate (including the zero-confidence-'no'
  // override) surfaces as the literal 'indeterminate'; yes/no keep title case.
  const answer = semantics.answer === 'indeterminate'
    ? 'indeterminate'
    : toLegacyAnswer(r?.answer)

  return {
    query: a.prompt ?? '',
    query_type: ((r?.query_type === 'action' ? 'action' : 'object')) as 'object' | 'action',
    answer,
    confidence: r?.confidence ?? null,
    detected_object: undefined,
    clips,
    detected_count: r?.detected_count !== undefined ? r.detected_count : semantics.detectedCount,
    indeterminate_reason: r?.indeterminate_reason !== undefined ? r.indeterminate_reason : semantics.indeterminateReason,
    term_confidences: r?.term_confidences ?? null,
    // PRI-552: terminal settlement summary. The SDK's AnalysisUsage type
    // predates metering_rule/frames — the wire response carries them (API
    // docs/openapi.json AnalysisUsage), so widen via cast until the SDK bumps.
    usage: (a.usage as PrimateVisionResult['usage']) ?? null,
  }
}

export async function getVisionResult(analysisId: string): Promise<PrimateVisionResult> {
  const a = await retrieveAnalysis(analysisId)
  return mapResult(a)
}

export async function cancelVisionJob(analysisId: string): Promise<{ cancelled: boolean }> {
  try {
    const a = await sdk.analyses.cancel(analysisId)
    return { cancelled: a.status === 'canceled' }
  } catch (e) {
    return toLegacyError(e)
  }
}

// ── Create ───────────────────────────────────────────────────────────────────

export interface CreatePublicAnalysisParams {
  video_id: string
  prompt: string
  /** Pre-compiled structured query — sent as `query` (parse_mode client), prompt omitted. */
  query?: CompiledQuery | Record<string, unknown> | null
  options?: Record<string, unknown>
}

/**
 * POST /v1/analyses. Returns the legacy run-prompt handle shape so
 * useAnalysis's dispatch branches keep working:
 *   {job_id, status: 'processing'|'complete', answer?, confidence?}.
 */
export async function createPublicAnalysis(params: CreatePublicAnalysisParams): Promise<{
  job_id: string
  status: string
  answer?: string
  confidence?: number
}> {
  try {
    const a = await sdk.analyses.create({
      video_id: params.video_id,
      ...(params.query
        ? { query: params.query as Record<string, unknown> }
        : { prompt: params.prompt }),
      ...(params.options ? { options: params.options } : {}),
    })
    const status = toLegacyStatus(a.status)
    return {
      job_id: a.id,
      status: status === 'complete' ? 'complete' : 'processing',
      ...(a.result?.answer != null ? { answer: toLegacyAnswer(a.result.answer) } : {}),
      ...(a.result?.confidence != null ? { confidence: a.result.confidence } : {}),
    }
  } catch (e) {
    return toLegacyError(e)
  }
}

// ── Session restore (replaces GET /v1/video/active-job) ──────────────────────

/**
 * Newest in-flight analysis, mapped to the legacy ActiveJob shape
 * ({job_id, status: 'running'|'queued', prompt, queue_position}).
 * Never throws — session restore is best-effort.
 */
export async function getActiveAnalysis(): Promise<ActiveJob | null> {
  try {
    // Best-effort probe on page load — no retries (SDK default is 3 with backoff).
    const analyzing = await sdk.analyses.list({ status: 'analyzing', limit: 1 }, { maxRetries: 0 })
    const active = analyzing.data[0]
      ?? (await sdk.analyses.list({ status: 'queued', limit: 1 }, { maxRetries: 0 })).data[0]
    if (!active) return null
    return {
      job_id: active.id,
      status: active.status === 'queued' ? 'queued' : 'running',
      prompt: active.prompt ?? undefined,
      queue_position: active.queue_position ?? undefined,
    }
  } catch {
    return null
  }
}

// ── Annotated result playback (replaces GET /v1/video/{id}/video) ────────────

/**
 * Signed annotated-video playback URL for an analysis.
 *  * an_ ids → Analysis.artifacts.annotated_video_url (sign-on-read, 1h TTL,
 *    no token needed — safe for <video src>).
 *  * legacy UUID job ids (pre-cutover history rows) → the internal endpoint
 *    URL (caller appends ?token=...), preserved until history rows age out.
 */
export async function getAnnotatedVideoUrl(analysisId: string): Promise<string | null> {
  if (!isPublicAnalysisId(analysisId)) {
    return internalApiUrl(`/v1/video/${analysisId}/video`)
  }
  const a = await retrieveAnalysis(analysisId)
  const artifacts = a.artifacts as { annotated_video_url?: string } | null
  return artifacts?.annotated_video_url ?? null
}

// ── Video upload (replaces presign-upload + uploadToS3 chain) ────────────────

/**
 * Full public upload: POST /v1/videos → S3 PUT (XHR when onProgress is
 * provided, fetch otherwise) → POST /v1/videos/{id}/complete.
 * Returns the ready Video (with media once signed).
 */
export async function uploadPublicVideo(
  file: File,
  opts: { onProgress?: (pct: number) => void } = {},
): Promise<Video> {
  const contentType: 'video/mp4' | 'video/quicktime' =
    file.type === 'video/quicktime' ? 'video/quicktime' : 'video/mp4'

  let created: Video
  try {
    created = await sdk.videos.create({
      filename: file.name,
      content_type: contentType,
      size_bytes: file.size,
    })
  } catch (e) {
    return toLegacyError(e)
  }

  const upload = created.upload
  if (!upload?.url) {
    throw Object.assign(new Error('Video create returned no upload envelope'), { code: 'upload_unavailable' })
  }

  if (opts.onProgress) {
    await putWithProgress(upload.url, file, upload.headers ?? {}, contentType, opts.onProgress)
  } else {
    const res = await fetch(upload.url, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': contentType, ...(upload.headers ?? {}) },
    })
    if (!res.ok) {
      throw Object.assign(new Error(`S3 upload failed: ${res.status}`), { status: res.status })
    }
  }

  try {
    return await sdk.videos.complete(created.id)
  } catch (e) {
    return toLegacyError(e)
  }
}

function putWithProgress(
  url: string,
  file: File,
  headers: Record<string, string>,
  contentType: string,
  onProgress: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    xhr.setRequestHeader('Content-Type', contentType)
    for (const [k, v] of Object.entries(headers)) {
      if (k.toLowerCase() !== 'content-type') xhr.setRequestHeader(k, v)
    }
    xhr.upload.onprogress = e => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100))
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve()
      else reject(Object.assign(new Error(`S3 upload failed: ${xhr.status}`), { status: xhr.status }))
    }
    xhr.onerror = () => reject(new Error('Network error during upload'))
    xhr.send(file)
  })
}

// ── Multipart one-shot (§4.3) — replaces legacy POST /v1/video/upload ─────────

const ONE_SHOT_MAX_BYTES = 100 * 1024 * 1024 // public one-shot cap (§4.3)

/** True when the file fits the public multipart one-shot path. */
export function fitsOneShot(file: File): boolean {
  return file.size <= ONE_SHOT_MAX_BYTES
}

/**
 * POST /v1/analyses as multipart one-shot ({video ≤100MB, prompt}) — the
 * public equivalent of the legacy /v1/video/upload path. The server creates
 * the video + analysis in one request. XHR for upload-progress events; auth
 * from the shared token registry (Clerk JWT or anon pv_live_ key).
 */
export async function createAnalysisMultipart(
  file: File,
  prompt: string,
  onProgress?: (pct: number) => void,
): Promise<{ job_id: string; status: string; video_id?: string }> {
  const { getAuthToken } = await import('./token')
  const token = await getAuthToken()
  const form = new FormData()
  form.append('video', file)
  form.append('prompt', prompt)

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${sdk.http.baseUrl}/v1/analyses`)
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    xhr.setRequestHeader('X-Primate-Client', 'web')

    xhr.upload.onprogress = e => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100))
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const a = JSON.parse(xhr.responseText) as Analysis
          resolve({
            job_id: a.id,
            status: toLegacyStatus(a.status) === 'complete' ? 'complete' : 'processing',
            video_id: a.video_id,
          })
        } catch {
          reject(new Error('Invalid JSON response'))
        }
      } else {
        try {
          const err = JSON.parse(xhr.responseText) as { error?: { message?: string; code?: string; details?: { upgrade_url?: string } } }
          reject(Object.assign(
            new Error(err?.error?.message ?? `Upload failed: ${xhr.status}`),
            { status: xhr.status, code: err?.error?.code, upgradeUrl: err?.error?.details?.upgrade_url },
          ))
        } catch {
          reject(Object.assign(new Error(`Upload failed: ${xhr.status}`), { status: xhr.status }))
        }
      }
    }
    xhr.onerror = () => reject(new Error('Network error during upload'))
    xhr.send(form)
  })
}

// ── Source playback + per-video history (GAP-1 consumption) ──────────────────

/** Fresh signed source-playback URL (Video.media, sign-on-read — re-fetch to re-sign). */
export async function getPublicVideoMediaUrl(videoId: string): Promise<string | null> {
  try {
    const v = await sdk.videos.retrieve(videoId)
    const media = (v as Video & { media?: { url: string; expires_at: string } | null }).media
    return media?.url ?? null
  } catch {
    return null
  }
}

/** GET /v1/videos/{id}/analyses — per-video result history (newest first). */
export async function listVideoAnalyses(
  videoId: string,
  params: { limit?: number; starting_after?: string; status?: string } = {},
): Promise<Page<Analysis>> {
  try {
    return await sdk.http.get<Page<Analysis>>(`/v1/videos/${videoId}/analyses`, params)
  } catch (e) {
    return toLegacyError(e)
  }
}

// ── Model catalog (PRI-496 Lane B: replaces internal GET /v1/model-info) ─────

export interface PublicModel {
  id: string
  object: 'model'
  status: 'stable' | 'preview' | 'deprecated'
  default: boolean
  capabilities: Record<string, unknown>
  sunset_at: string | null
  created_at: string
}

/**
 * The default public model from GET /v1/models. Checkpoint metadata
 * (vl_step/jepa_step) is intentionally absent — internal provenance is
 * admin-only (2026-06-26 leak fix); the console uses /v1/model-info-proxy.
 * Returns null on failure — callers render the static display name.
 */
export async function getDefaultPublicModel(): Promise<PublicModel | null> {
  try {
    const page = await sdk.models.list()
    const models = (page.data ?? []) as unknown as PublicModel[]
    return models.find(m => m.default) ?? models[0] ?? null
  } catch {
    return null
  }
}
